import { PencilLineIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { FormattedMessage, useIntl } from 'react-intl';
import type { FigureDefinition } from 'types/aiChatFigure';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { dateStringToDateObject } from 'utils/parseDate';
import { buildMessageClipboardData, draftMarkdown, writeClipboardData } from '../../../utils/messageClipboard';
import MessageTextPart from '../MessageTextPart';
import { FigureCopyButton } from './FigureCopyButton';
import { FigureFrame } from './FigureFrame';

interface Props {
    definition: FigureDefinition;
}

/**
 * A text the AI wrote for the user to send or save, such as an experience record or an email: its title, the day it
 * was written, the text in full as the answer draws its own, and Copy under it. Copy puts the text alone on the
 * clipboard as the answer's Copy does: HTML with its bold and lists for Word or Outlook, plain text for any other field.
 */
export function DraftFigure({ definition }: Props) {
    const intl = useIntl();
    const text = draftMarkdown(definition.text ?? '');
    const meta = definition.asOf ? (
        <FormattedMessage
            id="ai-chat.figure.draft.written"
            values={{ date: <FormattedDate key="date" value={dateStringToDateObject(definition.asOf)} /> }}
        />
    ) : null;

    return (
        <FigureFrame
            icon={PencilLineIcon}
            tileClassName={NEUTRAL_TILE_CLASS}
            title={definition.title}
            meta={meta}
            className="my-3"
        >
            <div className="border-divider-soft border-t px-4 pt-3 pb-2">
                <MessageTextPart text={text} />
            </div>
            <div className="flex items-center gap-1 px-2 pb-1.5">
                <FigureCopyButton
                    label={intl.formatMessage({ id: 'ai-chat.figure.draft.copy' })}
                    copy={() => writeClipboardData(buildMessageClipboardData([{ kind: 'text', key: 'draft', text }]))}
                />
            </div>
        </FigureFrame>
    );
}
